import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, ExternalLink, FileText, RotateCcw, Save, Search, Send, ShieldCheck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BALANCE_SHEET_TYPES = new Set(['asset', 'liability', 'equity', 'contra-asset', 'contra-liability']);

function parseAmount(value) {
    const amount = Number.parseFloat(String(value || '').replace(/,/g, ''));
    return Number.isFinite(amount) ? amount : 0;
}

function OpeningBalancesTab({
    selectedCourse,
    chartOfAccounts,
    onRefresh,
    fmt,
    isReadOnly,
    permissions,
    user,
    onNavigate,
    onSetTrialBalanceDate,
}) {
    const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
    const [reference, setReference] = useState('OPENING-BALANCE');
    const [query, setQuery] = useState('');
    const [showAllAccounts, setShowAllAccounts] = useState(false);
    const [balances, setBalances] = useState({});
    const [actionState, setActionState] = useState(null);
    const [lastResult, setLastResult] = useState(null);

    const canPost = Boolean(permissions?.can_post || user?.role === 'instructor' || user?.role === 'admin');
    const isStudent = user?.role === 'student';
    const isBusy = Boolean(actionState);

    const accounts = useMemo(() => {
        const flat = [];
        (chartOfAccounts || []).forEach((account) => {
            flat.push({
                code: account.code,
                title: account.title,
                type: account.type || '',
                parentCode: null,
            });
            (account.subs || []).forEach((sub) => {
                flat.push({
                    code: sub.code,
                    title: sub.title,
                    type: account.type || '',
                    parentCode: account.code,
                });
            });
        });
        return flat;
    }, [chartOfAccounts]);

    const visibleAccounts = useMemo(() => {
        const loweredQuery = query.trim().toLowerCase();
        return accounts.filter((account) => {
            const type = String(account.type || '').toLowerCase();
            const isBalanceSheet = BALANCE_SHEET_TYPES.has(type);
            const matchesType = showAllAccounts || isBalanceSheet;
            const matchesQuery = !loweredQuery
                || account.code?.toLowerCase().includes(loweredQuery)
                || account.title?.toLowerCase().includes(loweredQuery);
            return matchesType && matchesQuery;
        });
    }, [accounts, query, showAllAccounts]);

    const totals = useMemo(() => {
        return Object.values(balances).reduce((sum, row) => {
            sum.debit += parseAmount(row.debit);
            sum.credit += parseAmount(row.credit);
            return sum;
        }, { debit: 0, credit: 0 });
    }, [balances]);

    const difference = Math.round((totals.debit - totals.credit) * 100) / 100;
    const hasLines = Object.values(balances).some((row) => parseAmount(row.debit) > 0 || parseAmount(row.credit) > 0);
    const isBalanced = hasLines && Math.abs(difference) < 0.01;

    const updateAmount = (code, side, value) => {
        const cleaned = value.replace(/[^\d.]/g, '');
        setBalances((prev) => {
            const nextRow = { ...(prev[code] || { debit: '', credit: '' }) };
            nextRow[side] = cleaned;
            nextRow[side === 'debit' ? 'credit' : 'debit'] = cleaned ? '' : nextRow[side === 'debit' ? 'credit' : 'debit'];
            return {
                ...prev,
                [code]: nextRow,
            };
        });
    };

    const clearBalances = () => {
        setBalances({});
        toast.info('Opening balance worksheet cleared');
    };

    const saveOpeningBalances = async (nextAction = 'draft') => {
        if (isReadOnly) {
            toast.error('Read-only users cannot create opening balances');
            return;
        }
        if (!selectedCourse) {
            toast.error('Select a course before creating opening balances');
            return;
        }
        if (!asOfDate) {
            toast.error('As-of date is required');
            return;
        }
        if (!isBalanced) {
            toast.error('Opening balances must be balanced before saving');
            return;
        }
        if (nextAction === 'post' && !canPost) {
            toast.error('Only instructors or admins can post opening balances to GL and Trial Balance');
            return;
        }
        if (nextAction === 'post') {
            const confirmed = window.confirm(
                'Post opening balances to GL and Trial Balance now? Posted vouchers cannot be edited. Corrections must be handled with a reversal or adjusting JV.'
            );
            if (!confirmed) return;
        }

        const details = accounts
            .map((account) => {
                const row = balances[account.code] || {};
                return {
                    account_code: account.code,
                    account_title: account.title,
                    sub1: null,
                    sub2: null,
                    debit: parseAmount(row.debit),
                    credit: parseAmount(row.credit),
                };
            })
            .filter((line) => line.debit > 0 || line.credit > 0);

        let createdVoucher = null;
        let savedVoucher = null;
        setActionState(nextAction);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const createResponse = await axios.post(`${API}/journal-vouchers`, {
                course_id: selectedCourse,
                date: asOfDate,
                reference: reference.trim() || 'OPENING-BALANCE',
                particulars: `Opening balances as of ${asOfDate}`,
                details,
            }, { headers });
            createdVoucher = createResponse.data;

            const saveResponse = await axios.put(
                `${API}/journal-vouchers/${createResponse.data.id}/save`,
                {},
                { headers }
            );
            savedVoucher = saveResponse.data?.jv || createResponse.data;

            let finalJv = savedVoucher;
            let resultStatus = 'saved';
            let reviewState = isStudent
                ? 'Student saved draft. Submit it when ready so the instructor can review.'
                : 'Instructor/admin saved draft. It is not in the review queue yet.';
            let glImpact = 'Draft only. It does not affect General Ledger, Trial Balance, or reports yet.';
            let nextSafeAction = canPost
                ? 'Open Journal Voucher and post it when you are ready to affect reports.'
                : 'Open Journal Voucher and submit it for instructor review when ready.';
            let toastMessage = `Opening balance JV ${finalJv?.jv_number || 'saved'} was created and saved as a draft. Find it in Record > Journal Voucher. Status: Saved Draft. Next: ${canPost ? 'post it only when final' : 'submit it for instructor review'}. It does not affect GL, Trial Balance, or reports until posted.`;

            if (nextAction === 'submit') {
                const submitResponse = await axios.put(
                    `${API}/journal-vouchers/${createResponse.data.id}/submit`,
                    {},
                    { headers }
                );
                finalJv = submitResponse.data?.jv || finalJv;
                resultStatus = 'submitted';
                reviewState = 'Instructor/admin reviews next in the Instructor JV Review Queue.';
                glImpact = 'Submitted for review. It does not affect GL, Trial Balance, or reports until posted.';
                nextSafeAction = 'Wait for instructor approval, or revise if the JV is returned.';
                toastMessage = `Opening balance JV ${finalJv?.jv_number || ''} was submitted for instructor review. Find it in Record > Journal Voucher as Submitted; instructor/admin sees it in the Instructor JV Review Queue. Status: Submitted for Review. It does not affect GL, Trial Balance, or reports until approved and posted.`;
            }

            if (nextAction === 'post') {
                await axios.put(
                    `${API}/journal-vouchers/${createResponse.data.id}/post`,
                    {},
                    { headers }
                );
                const postedResponse = await axios.get(
                    `${API}/journal-vouchers/${createResponse.data.id}`,
                    { headers }
                );
                finalJv = postedResponse.data || { ...finalJv, status: 'posted' };
                resultStatus = 'posted';
                reviewState = 'No further reviewer is needed for this posted instructor/admin transaction.';
                glImpact = `Posted to GL. Trial Balance includes it when the report date is ${asOfDate} or later.`;
                nextSafeAction = 'Open Trial Balance and set the As-of Date to this opening balance date or later.';
                toastMessage = `Opening balance JV ${finalJv?.jv_number || ''} was posted. It now affects GL and appears in Trial Balance when the report As-of Date is ${asOfDate} or later. Status: Posted. Next: open Trial Balance with the matching date.`;
            }

            setBalances({});
            setLastResult({
                id: finalJv?.id || createResponse.data.id,
                jvNumber: finalJv?.jv_number || saveResponse.data?.jv?.jv_number || 'Saved JV',
                date: asOfDate,
                reference: reference.trim() || 'OPENING-BALANCE',
                status: resultStatus,
                amount: totals.debit,
                reviewState,
                glImpact,
                nextSafeAction,
            });
            if (onRefresh) await onRefresh();
            toast.success(toastMessage);
        } catch (error) {
            const detail = error.response?.data?.detail || 'Failed to process opening balances';
            if (savedVoucher || createdVoucher) {
                const partialVoucher = savedVoucher || createdVoucher;
                setLastResult({
                    id: partialVoucher?.id,
                    jvNumber: partialVoucher?.jv_number || 'Draft JV',
                    date: asOfDate,
                    reference: reference.trim() || 'OPENING-BALANCE',
                    status: partialVoucher?.status || 'draft',
                    amount: totals.debit,
                    reviewState: 'The JV exists, but the requested next step did not finish.',
                    glImpact: 'No GL, Trial Balance, or report impact until the JV is posted.',
                    nextSafeAction: 'Open Journal Voucher to review the saved work before retrying.',
                });
                if (onRefresh) await onRefresh();
                toast.error(`${detail}. Check Record > Journal Voucher before retrying.`);
            } else {
                toast.error(detail);
            }
        } finally {
            setActionState(null);
        }
    };

    const openJournalVoucher = () => {
        if (onNavigate) onNavigate('jv');
    };

    const openTrialBalance = () => {
        if (onSetTrialBalanceDate) onSetTrialBalanceDate(lastResult?.date || asOfDate);
        if (onNavigate) onNavigate('trial');
    };

    const statusBadgeClass = lastResult?.status === 'posted'
        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
        : lastResult?.status === 'submitted'
            ? 'bg-blue-100 text-blue-700 border-blue-200'
            : 'bg-amber-100 text-amber-700 border-amber-200';

    return (
        <div className="space-y-4" data-testid="opening-balances-tab">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">Opening Balance Setup</h2>
                    <p className="text-sm text-slate-500">
                        {isReadOnly
                            ? 'Review the beginning-balance worksheet and source Journal Voucher trail without changing records.'
                            : 'Use this guided worksheet to create the beginning-balance Journal Voucher and send it through the right review/posting path.'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isReadOnly ? (
                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="opening-balance-readonly-badge">
                            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Read-only review
                        </span>
                    ) : (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={clearBalances}
                                disabled={isBusy || !hasLines}
                                data-testid="opening-balance-clear"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" /> Clear
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => saveOpeningBalances('draft')}
                                disabled={isBusy || !isBalanced}
                                data-testid="opening-balance-save"
                            >
                                <Save className="w-4 h-4 mr-2" /> {actionState === 'draft' ? 'Saving...' : 'Save Draft JV'}
                            </Button>
                            {canPost ? (
                                <Button
                                    type="button"
                                    onClick={() => saveOpeningBalances('post')}
                                    disabled={isBusy || !isBalanced}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid="opening-balance-post"
                                >
                                    <ShieldCheck className="w-4 h-4 mr-2" /> {actionState === 'post' ? 'Posting...' : 'Post to GL/TB'}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    onClick={() => saveOpeningBalances('submit')}
                                    disabled={isBusy || !isBalanced}
                                    className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                                    data-testid="opening-balance-submit"
                                >
                                    <Send className="w-4 h-4 mr-2" /> {actionState === 'submit' ? 'Submitting...' : 'Save & Submit for Review'}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900" data-testid="opening-balance-explainer">
                <p className="font-semibold text-[#0F2A44]">Where this goes</p>
                <p className="mt-1">
                    Opening Balance Setup is not a separate ledger. It creates a Journal Voucher dated on the As-of Date. A saved draft appears in Record &gt; Journal Voucher only.
                    A submitted JV appears in the Instructor JV Review Queue. A posted JV affects General Ledger, Trial Balance, and reports.
                </p>
                <p className="mt-2 text-blue-800">
                    Trial Balance includes the opening balances only after posting and only when the report As-of Date is {asOfDate} or later.
                </p>
            </div>

            {lastResult && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="opening-balance-result">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="text-sm font-semibold text-slate-900">Opening balance trail</p>
                            <p className="text-sm text-slate-500">
                                {lastResult.jvNumber} was created for {lastResult.date} with reference {lastResult.reference}.
                            </p>
                        </div>
                        <span className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusBadgeClass}`}>
                            {lastResult.status}
                        </span>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">What changed</p>
                            <p className="mt-1 text-sm text-slate-900">Opening balance JV for {fmt(lastResult.amount)}.</p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Where to find it</p>
                            <p className="mt-1 text-sm text-slate-900">Record &gt; Journal Voucher, number {lastResult.jvNumber}.</p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Review next</p>
                            <p className="mt-1 text-sm text-slate-900">{lastResult.reviewState}</p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">GL / Trial Balance</p>
                            <p className="mt-1 text-sm text-slate-900">{lastResult.glImpact}</p>
                        </div>
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 md:col-span-2">
                            <p className="text-xs font-semibold uppercase text-slate-500">Next safe action</p>
                            <p className="mt-1 text-sm text-slate-900">{lastResult.nextSafeAction}</p>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button type="button" variant="outline" onClick={openJournalVoucher} data-testid="opening-balance-open-jv">
                            <ExternalLink className="w-4 h-4 mr-2" /> Open Journal Voucher
                        </Button>
                        {lastResult.status === 'posted' && (
                            <Button type="button" onClick={openTrialBalance} className="bg-[#2F8FCB] hover:bg-[#2577a8]" data-testid="opening-balance-open-trial">
                                <ExternalLink className="w-4 h-4 mr-2" /> Check Trial Balance
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1.2fr]">
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <Label htmlFor="opening-balance-date">As-of Date</Label>
                    <p className="text-xs text-slate-500 mt-1">
                        This is the cut-off date for the beginning balances. Trial Balance includes it when the report date is on or after this date.
                    </p>
                    <Input
                        id="opening-balance-date"
                        type="date"
                        value={asOfDate}
                        onChange={(event) => setAsOfDate(event.target.value)}
                        disabled={isReadOnly || isBusy}
                        className="mt-1"
                        data-testid="opening-balance-date"
                    />
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <Label htmlFor="opening-balance-reference">Reference</Label>
                    <Input
                        id="opening-balance-reference"
                        value={reference}
                        onChange={(event) => setReference(event.target.value)}
                        disabled={isReadOnly || isBusy}
                        className="mt-1"
                        data-testid="opening-balance-reference"
                    />
                </div>
                <div className={`border rounded-lg p-4 ${isBalanced ? 'bg-emerald-50 border-emerald-200' : hasLines ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
                    <div className="flex items-start gap-3">
                        {isBalanced ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        )}
                        <div className="flex-1">
                            <p className="font-semibold text-slate-900">
                                {isBalanced ? (canPost ? 'Balanced and ready to post' : 'Balanced and ready to submit') : hasLines ? 'Needs balancing' : 'No balances entered'}
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                                <div>
                                    <span className="block text-slate-500">Debit</span>
                                    <strong>{fmt(totals.debit)}</strong>
                                </div>
                                <div>
                                    <span className="block text-slate-500">Credit</span>
                                    <strong>{fmt(totals.credit)}</strong>
                                </div>
                                <div>
                                    <span className="block text-slate-500">Difference</span>
                                    <strong className={Math.abs(difference) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}>
                                        {fmt(Math.abs(difference))}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex flex-col gap-3 border-b border-slate-200 p-4 md:flex-row md:items-center md:justify-between">
                    <div className="relative md:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Search account code or title..."
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            className="pl-10"
                            data-testid="opening-balance-search"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                            type="checkbox"
                            checked={showAllAccounts}
                            onChange={(event) => setShowAllAccounts(event.target.checked)}
                            className="rounded border-slate-300"
                            data-testid="opening-balance-show-all"
                        />
                        Show income and expense accounts
                    </label>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                            <tr>
                                <th className="text-left px-4 py-3 w-28 whitespace-nowrap">Code</th>
                                <th className="text-left px-4 py-3">Account</th>
                                <th className="text-left px-4 py-3">Type</th>
                                <th className="text-right px-4 py-3">Debit</th>
                                <th className="text-right px-4 py-3">Credit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {visibleAccounts.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-10 text-center text-slate-500">
                                        <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                        No matching accounts found.
                                    </td>
                                </tr>
                            ) : (
                                visibleAccounts.map((account) => {
                                    const row = balances[account.code] || { debit: '', credit: '' };
                                    return (
                                        <tr key={account.code} className="hover:bg-slate-50">
                                            <td className="px-4 py-3 font-mono text-slate-900 whitespace-nowrap">{account.code}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{account.title}</div>
                                                {account.parentCode && (
                                                    <div className="text-xs text-slate-500">Sub-account of {account.parentCode}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 capitalize text-slate-600">{account.type}</td>
                                            <td className="px-4 py-3">
                                                <Input
                                                    inputMode="decimal"
                                                    value={row.debit}
                                                    onChange={(event) => updateAmount(account.code, 'debit', event.target.value)}
                                                    placeholder="0.00"
                                                    className="ml-auto w-36 text-right"
                                                    disabled={isReadOnly || isBusy}
                                                    data-testid={`opening-balance-debit-${account.code}`}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <Input
                                                    inputMode="decimal"
                                                    value={row.credit}
                                                    onChange={(event) => updateAmount(account.code, 'credit', event.target.value)}
                                                    placeholder="0.00"
                                                    className="ml-auto w-36 text-right"
                                                    disabled={isReadOnly || isBusy}
                                                    data-testid={`opening-balance-credit-${account.code}`}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default OpeningBalancesTab;
